import { Stack } from "@fluentui/react";
import { animated, useSpring } from "@react-spring/web";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import styles from "./Answer.module.css";
import reasoningStyles from "./AnswerLoading.module.css";
import { AnswerIcon } from "./AnswerIcon";

// These match the actual backend processing steps in chatreadretrieveread.py
const LOADING_STEPS = [
    "loadingStep1", // "Analyzing your question..."
    "loadingStep2", // "Searching knowledge base..."
    "loadingStep3"  // "Generating answer..."
];

const STEP_INTERVAL = 7000; // Change step every 7 seconds

// Insert a blank line before each **bold** title that is not already at the start of a line
function formatReasoningMarkdown(text: string): string {
    return text.replace(/(?<!\n)\s*(\*\*[^*]+\*\*)/g, "\n\n$1");
}

interface Props {
    reasoning?: string;
}

export const AnswerLoading = ({ reasoning }: Props) => {
    const { t, i18n } = useTranslation();
    const [stepIndex, setStepIndex] = useState(0);
    
    const animatedStyles = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 }
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setStepIndex(prevIndex => {
                // Cycle through steps, stay at last step if process takes longer
                if (prevIndex < LOADING_STEPS.length - 1) {
                    return prevIndex + 1;
                }
                return prevIndex;
            });
        }, STEP_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    return (
        <animated.div style={{ ...animatedStyles }}>
            <Stack className={styles.answerContainer} verticalAlign="space-between">
                <AnswerIcon />
                <Stack.Item grow>
                    {reasoning ? (
                        <div className={reasoningStyles.reasoningBlock}>
                            <span className={reasoningStyles.reasoningLabel}>Thinking…</span>
                            <div className={reasoningStyles.reasoningContent}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{formatReasoningMarkdown(reasoning)}</ReactMarkdown>
                                <span className={styles.loadingdots} />
                            </div>
                        </div>
                    ) : (
                        <p className={styles.answerText}>
                            {t(LOADING_STEPS[stepIndex])}
                            <span className={styles.loadingdots} />
                        </p>
                    )}
                </Stack.Item>
            </Stack>
        </animated.div>
    );
};
